/**
 * The county landing pages: `/fl/broward`, `/fl/miami-dade`, `/fl/st-lucie`.
 *
 * An ad for one county has to land on a page about that county — the ad's
 * promise and the page's first line have to match, or the click is paid for
 * and wasted. These pages are that: one per live county, addressed by a slug
 * of the county's name, public, and rendered from the same registry and
 * status endpoints the coverage map reads, so nothing on them is written per
 * county and nothing can go stale on its own.
 *
 * The slug is derived, never listed: a county added to the registry gets its
 * page the same morning.
 */

import { FLORIDA_COUNTY_SHAPES } from "./floridaShapes";

export const LANDING_PREFIX = "/fl/";

/** "St. Lucie" → "st-lucie", "Miami-Dade" → "miami-dade". */
export function countySlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[.']/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

const FIPS_BY_SLUG: Record<string, string> = Object.fromEntries(
  FLORIDA_COUNTY_SHAPES.map((shape) => [countySlug(shape.name), shape.fips]),
);

/** The FIPS a slug names, or "" for one Florida does not have. */
export function fipsForSlug(slug: string): string {
  return FIPS_BY_SLUG[slug] ?? "";
}

/** The path of a county's landing page. */
export function landingPath(name: string): string {
  return LANDING_PREFIX + countySlug(name);
}

/**
 * The slug in a pathname, or null when the path is not a landing page.
 * `/fl` and `/fl/` are the statewide page, returned as "".
 */
export function landingSlug(pathname: string): string | null {
  if (pathname === "/fl" || pathname === "/fl/") return "";
  if (!pathname.startsWith(LANDING_PREFIX)) return null;
  const rest = pathname.slice(LANDING_PREFIX.length).replace(/\/+$/, "");
  if (!rest || rest.includes("/")) return null;
  return rest.toLowerCase();
}

/**
 * Where the page's button goes: the signup form, opened on signup rather than
 * sign-in, with the county carried through so the first thing a new account
 * sees is the county the ad was about.
 */
export function signupPath(fips: string): string {
  return fips ? `/signin?county=${fips}&mode=signup` : "/signin?mode=signup";
}
